/**
 * Color Wheel Canvas Hook
 * Extracted canvas drawing logic for better performance and reusability
 */

import { useEffect, useRef, useCallback } from 'react';
import { colorEngine } from '../../lib/colorEngine';

interface UseColorWheelCanvasProps {
  size: number;
  colorWheelData: string[];
  selectedColor?: string;
  mode: 'normal' | 'learning' | 'exercise' | 'demonstration';
  harmonyColors?: string[];
}

export function useColorWheelCanvas({
  size,
  colorWheelData,
  selectedColor,
  mode,
  harmonyColors = []
}: UseColorWheelCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const drawColorWheel = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 10;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Draw color wheel segments
    colorWheelData.forEach((color, index) => {
      const startAngle = (index / colorWheelData.length) * Math.PI * 2 - Math.PI / 2;
      const endAngle = ((index + 1) / colorWheelData.length) * Math.PI * 2 - Math.PI / 2;

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    });

    // Draw harmony lines if in learning mode
    if (mode === 'learning' && selectedColor && harmonyColors.length > 0) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 3;
      ctx.setLineDash([5, 5]);

      harmonyColors.forEach((color) => {
        const parsedColor = colorEngine.parseColor(color);
        if (parsedColor?.hsl?.h !== undefined) {
          const angle = (parsedColor.hsl.h / 360) * Math.PI * 2 - Math.PI / 2;
          const endX = centerX + Math.cos(angle) * radius;
          const endY = centerY + Math.sin(angle) * radius;

          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }
      });

      ctx.setLineDash([]);
    }

    // Draw selected color indicator
    if (selectedColor) {
      const parsedColor = colorEngine.parseColor(selectedColor);
      if (parsedColor?.hsl?.h !== undefined) {
        const angle = (parsedColor.hsl.h / 360) * Math.PI * 2 - Math.PI / 2;
        const indicatorX = centerX + Math.cos(angle) * (radius - 15);
        const indicatorY = centerY + Math.sin(angle) * (radius - 15);

        ctx.beginPath();
        ctx.arc(indicatorX, indicatorY, 8, 0, Math.PI * 2);
        ctx.fillStyle = 'white';
        ctx.fill();
        ctx.strokeStyle = selectedColor;
        ctx.lineWidth = 3;
        ctx.stroke();
      }
    }
  }, [size, colorWheelData, selectedColor, mode, harmonyColors]);

  // Auto-draw when dependencies change
  useEffect(() => {
    drawColorWheel();
  }, [drawColorWheel]);

  return { canvasRef, drawColorWheel };
}
